import { Contact, IContact } from '@app/models/Contact'
import { EarningsSummary, IEarningsSummary } from '@app/models/EarningsSummary'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, take, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export enum UserViewScope {
  Roles = 'Roles',
  Teams = 'Teams',
  Permissions = 'Permissions',
  Access = 'Access',
}

export interface IUserViewState {
  readonly error: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly item: IContact
  readonly permissions: IEntityPermissions
  readonly scope: UserViewScope
  readonly showConfirmDelete: boolean
}

const UserViewRecord = Record({
  error: '',
  isDeleting: false,
  isLoading: false,
  item: new Contact(),
  permissions: new EntityPermissions(),
  scope: UserViewScope.Roles,
  showConfirmDelete: false,
})

export class UserViewState extends UserViewRecord implements IUserViewState {
  public static fromJS(initialUserView?: Partial<IUserViewState>) {
    return new UserViewState(initialUserView)
  }

  public readonly error: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly item: Contact
  public readonly permissions: EntityPermissions
  public readonly scope: UserViewScope
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<IUserViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteUser = 'USER_VIEW::DELETE',
  DeleteUserFailure = 'USER_VIEW::DELETE_FAILURE',
  DeleteUserRequest = 'USER_VIEW::DELETE_REQUEST',
  DeleteUserSuccess = 'USER_VIEW::DELETE_SUCCESS',
  LoadUser = 'USER_VIEW::LOAD_USER',
  LoadUserRequest = 'USER_VIEW::LOAD_USER_REQUEST',
  LoadUserSuccess = 'USER_VIEW::LOAD_USER_SUCCESS',
  LoadUserFailure = 'USER_VIEW::LOAD_USER_FAILURE',
  ResetUserView = 'USER_VIEW::RESET_STATE',
  SetScope = 'USER_VIEW::SET_SCOPE',
  ToggleConfirmDelete = 'USER_VIEW::TOGGLE_CONFIRM_DELETE',
}

interface IDeleteUserAction {
  connectionId: string
  type: ActionTypes.DeleteUser
}

interface IDeleteUserFailureAction {
  error: string
  type: ActionTypes.DeleteUserFailure
}

interface IDeleteUserRequestAction {
  type: ActionTypes.DeleteUserRequest
}

interface IDeleteUserSuccessAction {
  type: ActionTypes.DeleteUserSuccess
}

interface ILoadUserFailureAction {
  error: string
  type: ActionTypes.LoadUserFailure
}

interface ILoadUserRequestAction {
  type: ActionTypes.LoadUserRequest
}

interface ILoadUserSuccessAction {
  item: Contact
  permissions: EntityPermissions
  type: ActionTypes.LoadUserSuccess
}

interface ILoadUserViewAction {
  type: ActionTypes.LoadUser
  value: string
}

interface IResetUserViewAction {
  type: ActionTypes.ResetUserView
}

interface ISetScopeAction {
  scope: UserViewScope
  type: ActionTypes.SetScope
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IDeleteUserFailureAction
  | IDeleteUserRequestAction
  | IDeleteUserSuccessAction
  | ILoadUserFailureAction
  | ILoadUserRequestAction
  | ILoadUserSuccessAction
  | IResetUserViewAction
  | ISetScopeAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteUser(connectionId: string): IDeleteUserAction {
  return {
    connectionId,
    type: ActionTypes.DeleteUser,
  }
}

function deleteUserFailure(error: string): IDeleteUserFailureAction {
  return {
    error,
    type: ActionTypes.DeleteUserFailure,
  }
}

function deleteUserRequest(): IDeleteUserRequestAction {
  return {
    type: ActionTypes.DeleteUserRequest,
  }
}

function deleteUserSuccess(): IDeleteUserSuccessAction {
  return {
    type: ActionTypes.DeleteUserSuccess,
  }
}

function loadUser(value: string): ILoadUserViewAction {
  return { type: ActionTypes.LoadUser, value }
}

function loadUserFailure(error: string): ILoadUserFailureAction {
  return { error, type: ActionTypes.LoadUserFailure }
}

function loadUserRequest(): ILoadUserRequestAction {
  return { type: ActionTypes.LoadUserRequest }
}

function loadUserSuccess(
  item: Contact,
  permissions: EntityPermissions,
): ILoadUserSuccessAction {
  return {
    item,
    permissions,
    type: ActionTypes.LoadUserSuccess,
  }
}

function resetUserView(): IResetUserViewAction {
  return { type: ActionTypes.ResetUserView }
}

function setScope(scope: UserViewScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  deleteUser,
  loadUser,
  resetUserView,
  setScope,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<UserViewState> = (
  state: UserViewState = new UserViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.DeleteUserFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as UserViewState

    case ActionTypes.DeleteUserRequest:
      return state.set('isDeleting', true) as UserViewState

    case ActionTypes.DeleteUserSuccess:
      return state
        .set('isDeleting', false)
        .set('item', new Contact()) as UserViewState

    case ActionTypes.LoadUserFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as UserViewState

    case ActionTypes.LoadUserRequest:
      return state.set('isLoading', true) as UserViewState

    case ActionTypes.LoadUserSuccess:
      return state
        .set('isLoading', false)
        .set('item', action.item)
        .set('permissions', action.permissions) as UserViewState

    case ActionTypes.ResetUserView:
      return state.set('isLoading', false) as UserViewState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as UserViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as UserViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteUserSaga(action: IDeleteUserAction) {
  try {
    yield put(deleteUserRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Contacts/${action.connectionId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/connections'))
      yield put(deleteUserSuccess())
    } else {
      yield put(deleteUserFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteUserFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadUserSaga(action: ILoadUserViewAction) {
  try {
    yield put(loadUserRequest())

    const baseUrl = `/api/Users/${action.value}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as Contact

      yield put(
        loadUserSuccess(
          Contact.fromJS(entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadUserFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteUser() {
  yield takeLatest(ActionTypes.DeleteUser, deleteUserSaga)
}

function* watchLoadUser() {
  yield takeLatest(ActionTypes.LoadUser, loadUserSaga)
}

export function* userViewRootSaga() {
  yield fork(watchDeleteUser)
  yield fork(watchLoadUser)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): UserViewState =>
  state.userView

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItem = () =>
  createSelector(selectModuleState(), (mState) => mState.item)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
